(() => {
  if (customElements.get('hollow-product-reviews')) return;
  const cache = new Map();
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined && text !== null) element.textContent = String(text);
    return element;
  };
  const text = value => typeof value === 'string' ? value : '';
  const number = value => value !== null && value !== '' && Number.isFinite(Number(value)) ? Number(value) : null;
  const formatCount = value => new Intl.NumberFormat('en-US').format(value);
  const safeMedia = value => {
    if (typeof value !== 'string' || !value.startsWith('https://')) return null;
    try {
      const url = new URL(value, window.location.href);
      return url.protocol === 'https:' && (url.hostname === window.location.hostname || url.hostname === 'cdn.shopify.com') ? url.href : null;
    } catch { return null; }
  };
  class HollowProductReviews extends HTMLElement {
    connectedCallback() {
      if (this.started) { if (!this.loaded && !this.loading) this.observe(); if (this.records?.length) this.startDateTimer(); return; }
      this.started = true;
      this.strings = JSON.parse(this.querySelector('[data-reviews-strings]').textContent);
      this.filters = this.querySelector('[data-reviews-filters]');
      this.search = this.querySelector('[data-reviews-search]');
      this.ratingFilter = this.querySelector('[data-reviews-rating]');
      this.sort = this.querySelector('[data-reviews-sort]');
      this.mediaFilter = this.querySelector('[data-reviews-media]');
      this.reset = this.querySelector('[data-reviews-reset]');
      this.reset.addEventListener('click', () => { this.search.value = ''; this.ratingFilter.value = 'all'; this.mediaFilter.checked = false; this.sort.value = 'mostRecent'; this.refresh(); });
      this.list = this.querySelector('[data-reviews-list]');
      this.more = this.querySelector('[data-reviews-more]');
      this.status = this.querySelector('[data-reviews-status]');
      this.gallery = this.querySelector('[data-reviews-gallery]');
      this.gallery.addEventListener('dragstart', event => event.preventDefault());
      this.gallery.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.drag = { id: event.pointerId, x: event.clientX, scroll: this.gallery.scrollLeft, moved: false };
      });
      this.gallery.addEventListener('pointermove', event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        if (!(event.buttons & 1)) { this.drag = null; return; }
        const delta = event.clientX - this.drag.x;
        if (Math.abs(delta) > 6 && !this.drag.moved) { this.drag.moved = true; this.gallery.setPointerCapture?.(event.pointerId); }
        if (!this.drag.moved) return;
        event.preventDefault(); this.gallery.scrollLeft = this.drag.scroll - delta;
      });
      const endDrag = event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        if (this.drag.moved) this.suppressGalleryClickUntil = Date.now() + 500;
        if (this.gallery.hasPointerCapture?.(event.pointerId)) this.gallery.releasePointerCapture(event.pointerId);
        this.drag = null;
      };
      this.gallery.addEventListener('pointerup', endDrag);
      this.gallery.addEventListener('pointercancel', endDrag);
      this.gallery.addEventListener('lostpointercapture', endDrag);
      this.gallery.addEventListener('pointerleave', event => { if (this.drag && !this.drag.moved) endDrag(event); });
      this.gallery.addEventListener('click', event => { if (Date.now() < (this.suppressGalleryClickUntil || 0)) { event.preventDefault(); event.stopPropagation(); } }, true);
      this.filterToggle = this.querySelector('[data-reviews-filter-toggle]');
      this.filterToggle.addEventListener('click', () => {
        const expanded = this.filterToggle.getAttribute('aria-expanded') !== 'true';
        this.filterToggle.setAttribute('aria-expanded', String(expanded));
        this.filters.classList.toggle('is-open', expanded);
      });
      this.pageSize = Math.min(20, Math.max(5, Number(this.dataset.pageSize) || 5));
      this.visible = this.pageSize;
      this.records = [];
      this.filters.addEventListener('submit', event => event.preventDefault());
      this.search.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.refresh(), 120);
      });
      this.ratingFilter.addEventListener('change', () => this.refresh());
      this.sort.addEventListener('change', () => this.refresh());
      this.mediaFilter.addEventListener('change', () => this.refresh());
      this.more.addEventListener('click', () => {
        const previous = this.list.children.length;
        this.visible += this.pageSize;
        this.render();
        this.list.children[previous]?.focus({ preventScroll: true });
      });
      this.tabs = [...this.querySelectorAll('[data-reviews-tab]')];
      for (const tab of this.tabs) {
        tab.addEventListener('click', () => this.selectTab(tab));
        tab.addEventListener('keydown', event => {
          if (event.altKey || event.ctrlKey || event.metaKey) return;
          const index = this.tabs.indexOf(tab), next = event.key === 'ArrowRight' ? this.tabs[(index + 1) % this.tabs.length] : event.key === 'ArrowLeft' ? this.tabs[(index + this.tabs.length - 1) % this.tabs.length] : event.key === 'Home' ? this.tabs[0] : event.key === 'End' ? this.tabs.at(-1) : null;
          if (next) { event.preventDefault(); this.selectTab(next); next.focus(); }
        });
      }
      this.observe();
    }
    observe() {
      this.observer?.disconnect();
      if (this.loaded || this.loading) return;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
        }, { rootMargin: '600px' });
        this.observer.observe(this);
      } else this.load();
    }
    disconnectedCallback() { this.observer?.disconnect(); clearTimeout(this.searchTimer); clearInterval(this.dateTimer); this.drag = null; this.closePreview(); }
    label(key, values = {}) {
      return Object.entries(values).reduce((result, [name, value]) => result.replaceAll(`__${name.toUpperCase()}__`, String(value)), this.strings[key] || '');
    }
    async load() {
      if (this.loaded || this.loading) return;
      this.loading = true;
      this.setAttribute('aria-busy', 'true');
      try {
        const url = this.dataset.reviewsUrl;
        if (!cache.has(url)) cache.set(url, fetch(url, { credentials: 'same-origin' }).then(response => { if (!response.ok) throw new Error('Reviews unavailable'); return response.json(); }).catch(error => { cache.delete(url); throw error; }));
        const data = await cache.get(url);
        if (!this.isConnected) return;
        const product = data.product || (this.dataset.productHandle === 'all' ? data.all : data.products?.[this.dataset.productHandle]);
        if (!product) throw new Error('Product review data unavailable');
        this.groups = product.groups;
        this.install(product);
        this.loaded = true;
      } catch {
        this.status.classList.remove('hollow-reviews__sr-only');
        this.status.textContent = this.strings.unavailable;
      } finally { this.loading = false; this.removeAttribute('aria-busy'); }
    }
    selectTab(tab) {
      if (!this.groups?.[tab.dataset.reviewsTab]) return;
      for (const item of this.tabs) { item.setAttribute('aria-selected', String(item === tab)); item.tabIndex = item === tab ? 0 : -1; }
      this.querySelector('[role="tabpanel"]').setAttribute('aria-labelledby', tab.id);
      this.search.value = ''; this.ratingFilter.value = 'all'; this.sort.value = 'mostRecent'; this.mediaFilter.checked = false;
      this.mediaFilter.closest('label').hidden = tab.dataset.reviewsTab === 'store';
      this.install(this.groups[tab.dataset.reviewsTab]);
    }
    install(product) {
        this.closePreview();
        clearInterval(this.dateTimer);
        this.hidden = false;
        this.capturedAt = product.capturedAt;
        if (text(product.title)) this.querySelector('.hollow-reviews__title').textContent = product.title;
        const seen = new Set();
        this.records = (product.reviews || []).filter(review => {
          const rating = number(review.rating), id = text(review.id);
          if (!id || seen.has(id) || rating === null || rating < 1 || rating > 5 || !Number.isInteger(rating)) return false;
          seen.add(id);
          return true;
        }).slice(0, this.dataset.productHandle === 'all' ? Infinity : 100).map((review, index) => ({ ...review, rating: Number(review.rating), order: index }));
        this.summary(product.summary || {});
        if (product.displaySummary === false) this.querySelector('[data-reviews-summary]').hidden = true;
        this.prepareGallery(product.gallery || []);
        this.gallery.replaceChildren(...(product.gallery || []).map(item => this.image(item, true)).filter(Boolean));
        this.gallery.hidden = !this.gallery.children.length;
        this.classList.toggle('has-gallery', !this.gallery.hidden);
        this.filters.hidden = !this.records.length;
        this.filterToggle.hidden = !this.records.length;
        if (!this.records.length) {
          this.list.replaceChildren();
          this.more.hidden = this.reset.hidden = true;
          this.status.classList.remove('hollow-reviews__sr-only');
          this.status.textContent = this.strings.unavailable;
          if (number(product.summary?.count) === null && number(product.summary?.rating) === null) this.hidden = true;
        } else { this.refresh(); this.startDateTimer(); }
    }
    relativeAge(label, capturedAt, now = Date.now()) {
      const captured = Date.parse(capturedAt);
      if (!Number.isFinite(captured) || !Number.isFinite(now) || now <= captured) return label;
      const special = { yesterday: [1, 'day'], today: [0, 'day'], 'last week': [1, 'week'], 'last month': [1, 'month'], 'last year': [1, 'year'] };
      const match = label.toLowerCase().trim().match(/^(\d+) (minute|hour|day|week|month|year)s? ago$/);
      const observed = match ? [Number(match[1]), match[2]] : special[label.toLowerCase().trim()];
      if (!observed) return label;
      // Age the observed interval; never synthesize a publication timestamp.
      const units = { minute: 60000, hour: 3600000, day: 86400000, week: 604800000, month: 2629800000, year: 31557600000 };
      const [originalAmount, originalUnit] = observed;
      const age = originalAmount * units[originalUnit] + now - captured;
      let unit = originalUnit;
      if (originalUnit === 'minute' || originalUnit === 'hour') {
        if (age >= units.day) unit = 'day';
        else if (age >= units.hour) unit = 'hour';
      }
      const amount = Math.floor(age / units[unit]);
      if (unit === originalUnit && amount === originalAmount) return label;
      const relative = new Intl.RelativeTimeFormat('en-US', { numeric: 'always' }).format(-amount, unit);
      return this.label('approximateAge', { age: relative });
    }
    startDateTimer() {
      clearInterval(this.dateTimer);
      if (!this.isConnected || !this.records?.length) return;
      this.dateTimer = setInterval(() => this.updateDates(), 60000);
    }
    oldestSortTime(review) {
      const absolute = Date.parse(review.createdAt);
      if (Number.isFinite(absolute)) return absolute;
      const captured = Date.parse(review.capturedAt || this.capturedAt);
      if (!Number.isFinite(captured)) return null;
      const label = text(review.dateLabel).toLowerCase().trim();
      const special = { yesterday: [1, 'day'], today: [0, 'day'], 'last week': [1, 'week'], 'last month': [1, 'month'], 'last year': [1, 'year'] };
      const match = label.match(/^(\d+) (minute|hour|day|week|month|year)s? ago$/);
      const observed = match ? [Number(match[1]), match[2]] : special[label];
      if (!observed) return null;
      const units = { minute: 60000, hour: 3600000, day: 86400000, week: 604800000, month: 2629800000, year: 31557600000 };
      const time = captured - observed[0] * units[observed[1]];
      return Number.isFinite(time) ? time : null;
    }
    updateDates() {
      for (const date of this.querySelectorAll('[data-relative-review-date]')) date.textContent = this.relativeAge(date.dataset.relativeReviewDate, date.dataset.capturedAt);
    }
    stars(rating, labelRating = rating) {
      const row = node('div', 'hollow-reviews__stars');
      row.setAttribute('role', 'img');
      row.setAttribute('aria-label', this.label('stars', { rating: labelRating }));
      for (let i = 0; i < 5; i++) {
        const star = node('span', 'hollow-reviews__star');
        star.setAttribute('aria-hidden', 'true');
        const fraction = rating - i;
        const fill = fraction <= 0 ? 0 : fraction >= 1 ? 100 : 50 + 45.4 * (fraction - 0.5);
        star.style.setProperty('--fill', `${fill}%`);
        star.append(node('span'));
        row.append(star);
      }
      return row;
    }
    summary(summary) {
      const rating = number(summary.rating), count = number(summary.count);
      this.querySelector('[data-reviews-summary]').hidden = rating === null && count === null;
      const score = this.querySelector('[data-reviews-average]');
      score.hidden = rating === null;
      if (rating !== null) score.textContent = text(summary.ratingLabel) || String(rating);
      const histogram = summary.distribution;
      const exactRating = histogram && count ? Object.entries(histogram).reduce((sum, [value, amount]) => sum + Number(value) * Number(amount), 0) / count : rating;
      this.querySelector('[data-reviews-average-stars]').replaceChildren(...(rating === null ? [] : [this.stars(exactRating, rating)]));
      const countElement = this.querySelector('[data-reviews-total]');
      countElement.hidden = count === null;
      if (count !== null) countElement.textContent = this.label('reviewCount', { count: formatCount(count) });
      const bars = this.querySelector('[data-reviews-distribution]');
      bars.replaceChildren();
      const distribution = summary.distribution;
      const valid = distribution && [1, 2, 3, 4, 5].every(value => Number.isInteger(number(distribution[value])) && number(distribution[value]) >= 0) && [1, 2, 3, 4, 5].reduce((sum, value) => sum + Number(distribution[value]), 0) === count;
      bars.hidden = !valid;
      if (!valid) return;
      const total = [1, 2, 3, 4, 5].reduce((sum, value) => sum + Number(distribution[value]), 0);
      for (let value = 5; value >= 1; value--) {
        const row = node('button', 'hollow-reviews__bar-row');
        row.type = 'button';
        row.dataset.rating = String(value);
        row.setAttribute('aria-label', this.label('starFilter', { count: value }) + ', ' + this.label('reviewCount', { count: formatCount(distribution[value]) }));
        row.setAttribute('aria-pressed', 'false');
        row.disabled = Number(distribution[value]) === 0;
        row.append(node('span', 'hollow-reviews__bar-label', value));
        const bar = node('span', 'hollow-reviews__bar'), fill = node('span', 'hollow-reviews__bar-fill');
        fill.style.setProperty('--percent', `${total ? Math.round(Number(distribution[value]) / total * 100) : 0}%`);
        bar.setAttribute('aria-hidden', 'true');
        bar.append(fill); row.append(bar);
        row.addEventListener('click', () => { this.ratingFilter.value = this.ratingFilter.value === String(value) ? 'all' : String(value); this.refresh(); });
        bars.append(row);
      }
    }
    refresh() { this.visible = this.pageSize; this.render(); }
    render() {
      const terms = this.search.value.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
      const rating = this.ratingFilter.value;
      this.reset.hidden = !terms.length && rating === 'all' && !this.mediaFilter.checked;
      for (const select of [this.sort, this.ratingFilter]) select.nextElementSibling.textContent = select.dataset.prefix + (select.value === 'all' ? '' : ': ' + select.selectedOptions[0].textContent);
      const availableRecords = this.mediaFilter.checked ? this.galleryEntries.filter(entry => text(entry.review?.id) && Number.isInteger(number(entry.review.rating)) && number(entry.review.rating) >= 1 && number(entry.review.rating) <= 5).map((entry, index) => ({ ...entry.review, rating: Number(entry.review.rating), media: entry.media, order: index })) : this.records;
      const records = availableRecords.filter(review => (rating === 'all' || review.rating === Number(rating)) && (!this.mediaFilter.checked || review.media?.length) && terms.every(term => `${text(review.title)} ${text(review.body)} ${text(review.author)}`.toLocaleLowerCase().includes(term)));
      const date = review => Number.isFinite(Date.parse(review.createdAt)) ? Date.parse(review.createdAt) : 0;
      const oldestTimes = new Map(this.sort.value === 'oldest' ? records.map(review => [review, this.oldestSortTime(review)]) : []);
      const oldest = (a, b) => {
        const left = oldestTimes.get(a), right = oldestTimes.get(b);
        if (left === null || right === null) return Number(left === null) - Number(right === null) || a.order - b.order;
        return left - right || b.order - a.order;
      };
      records.sort((a, b) => this.sort.value === 'oldest' ? oldest(a, b) : this.sort.value === 'mostHelpful' ? (number(b.helpfulVotes) || 0) - (number(a.helpfulVotes) || 0) || a.order - b.order : this.sort.value === 'highestRating' ? b.rating - a.rating || date(b) - date(a) || a.order - b.order : this.sort.value === 'lowestRating' ? a.rating - b.rating || date(b) - date(a) || a.order - b.order : Number(b.pinned === true) - Number(a.pinned === true) || date(b) - date(a) || a.order - b.order);
      const extra = this.sort.value === 'mostRecent' && this.dataset.productHandle !== 'all' ? records.filter(review => review.pinned === true).length : 0;
      const visible = Math.min(this.visible + extra, records.length);
      this.list.replaceChildren(...records.slice(0, visible).map(review => this.card(review)));
      this.more.hidden = visible >= records.length;
      this.status.classList.toggle('hollow-reviews__sr-only', records.length > 0);
      this.status.textContent = records.length ? this.label('showing', { visible, count: records.length }) : this.strings.noResults;
      for (const bar of this.querySelectorAll('[data-rating]')) bar.setAttribute('aria-pressed', String(bar.dataset.rating === rating));
    }
    card(review, modal = false) {
      const article = node('article', 'hollow-reviews__item');
      article.dataset.reviewId = review.id;
      article.tabIndex = -1;
      const author = node('div', 'hollow-reviews__author');
      const identity = node('div', 'hollow-reviews__identity');
      if (text(review.initials)) { const avatar = node('div', 'hollow-reviews__avatar'); avatar.setAttribute('aria-hidden', 'true'); avatar.append(node('span', '', review.initials)); author.append(avatar); }
      if (text(review.author)) identity.append(node('h3', 'hollow-reviews__name', review.author));
      if (review.verified === true) identity.append(node('p', 'hollow-reviews__verified', this.strings.verified));
      author.append(identity);
      const content = node('div', 'hollow-reviews__content'), head = node('div', 'hollow-reviews__review-head');
      head.append(this.stars(review.rating));
      if (text(review.dateLabel) || text(review.createdAt)) {
        const hasAbsoluteDate = Number.isFinite(Date.parse(review.createdAt));
        const time = node(hasAbsoluteDate ? 'time' : 'span', 'hollow-reviews__date', review.dateLabel || review.createdAt);
        if (hasAbsoluteDate) {
          time.dateTime = review.createdAt;
          if (!review.dateLabel) time.textContent = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(review.createdAt));
        } else if (text(review.dateLabel) && Number.isFinite(Date.parse(review.capturedAt || this.capturedAt))) {
          time.dataset.relativeReviewDate = review.dateLabel;
          time.dataset.capturedAt = review.capturedAt || this.capturedAt;
          time.textContent = this.relativeAge(review.dateLabel, time.dataset.capturedAt);
          const capturedDate = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(new Date(time.dataset.capturedAt));
          time.title = this.label('capturedDate', { date: capturedDate, label: review.dateLabel });
        }
        head.append(time);
      }
      if (review.pinned === true) { const pin = node('span', 'hollow-reviews__pin'); pin.setAttribute('role', 'img'); pin.setAttribute('aria-label', this.strings.pinned); head.append(pin); }
      content.append(head);
      if (text(review.title)) content.append(node('h4', 'hollow-reviews__review-title', review.title));
      if (text(review.body)) content.append(node('p', 'hollow-reviews__body', review.body));
      if (modal) {
        for (const trait of review.traits || []) {
          const value = number(trait?.value), maximum = number(trait?.scaleMax);
          if (!text(trait?.label) || value === null || maximum === null || maximum < 2 || maximum > 10 || value < 1 || value > maximum) continue;
          const row = node('div', 'hollow-reviews__dialog-trait');
          row.append(node('strong', '', trait.label));
          const scale = node('div', 'hollow-reviews__dialog-scale');
          scale.setAttribute('role', 'img'); scale.setAttribute('aria-label', this.label('traitRating', { label: trait.label, value, max: maximum }));
          scale.style.setProperty('--trait-value', `${(value - 1) / (maximum - 1) * 100}%`);
          scale.style.setProperty('--trait-count', maximum);
          for (let index = 0; index < maximum; index++) { const segment = node('span'); segment.setAttribute('aria-hidden', 'true'); scale.append(segment); }
          const labels = node('div', 'hollow-reviews__dialog-scale-labels');
          labels.append(node('span', '', text(trait.minLabel)), node('span', '', text(trait.centerLabel)), node('span', '', text(trait.maxLabel)));
          row.append(scale, labels); content.append(row);
        }
      }
      const media = node('div', 'hollow-reviews__media');
      for (const item of (modal ? [] : review.media || []).slice(0, 8)) {
        const button = this.image(item, false, review); if (button) media.append(button);
      }
      if (media.children.length) content.append(media);
      if (modal && (text(review.variant) || text(review.productTitle) || text(review.variantTitle))) content.append(node('p', 'hollow-reviews__variant', text(review.variant) || this.label('reviewFor', { product: [text(review.productTitle), text(review.variantTitle)].filter(Boolean).join(' — ') })));
      if (text(review.recommendation)) content.append(node('p', 'hollow-reviews__recommendation' + (review.recommendation === 'Would not recommend' ? ' hollow-reviews__recommendation--negative' : ''), review.recommendation));
      article.append(author, content);
      return article;
    }
    mediaKey(item) { return text(item.sourceUrl) || text(item.destinationUrl) || text(item.url) || text(item.fullSizeUrl); }
    prepareGallery(items) {
      this.galleryEntries = [];
      const recordsByMedia = new Map();
      for (const review of this.records) for (const item of review.media || []) recordsByMedia.set(this.mediaKey(item), review);
      for (const item of items) this.addGalleryItem(item, item.modalReview || recordsByMedia.get(this.mediaKey(item)));
      for (const review of this.records) for (const item of review.media || []) this.addGalleryItem(item, item.modalReview || review);
    }
    addGalleryItem(item, review) {
      if (!safeMedia(item.destinationUrl || item.url || item.fullSizeUrl)) return null;
      const key = review?.id ? `review:${review.id}` : `media:${this.mediaKey(item)}`;
      let entry = this.galleryEntries.find(value => value.key === key || value.media.some(media => this.mediaKey(media) === this.mediaKey(item)));
      if (!entry) { entry = { key, review, media: [] }; this.galleryEntries.push(entry); }
      if (!entry.review && review) entry.review = review;
      const media = [item, ...(review?.media || [])];
      for (const value of media) {
        if (!safeMedia(value.destinationUrl || value.url || value.fullSizeUrl)) continue;
        const existing = entry.media.findIndex(candidate => this.mediaKey(candidate) === this.mediaKey(value));
        if (existing < 0) entry.media.push(value);
        else entry.media[existing] = { ...entry.media[existing], ...value };
      }
      return entry;
    }
    image(item, gallery = false, review = item.modalReview) {
      review = item.modalReview || review;
      const src = safeMedia(item.destinationUrl || item.url || item.fullSizeUrl);
      if (!src) return null;
      const button = node('button', gallery ? 'hollow-reviews__gallery-item' : 'hollow-reviews__image'); button.type = 'button';
      if (item.kind === 'video' && safeMedia(item.videoUrl)) button.classList.add('has-video');
      const img = node('img'); img.src = src; img.alt = text(item.alt) || this.strings.imageAlt; img.loading = 'lazy'; img.width = Number(item.width) || 96; img.height = Number(item.height) || 96;
      button.setAttribute('aria-label', this.label('openMedia', { title: text(review?.title) || img.alt }));
      button.setAttribute('aria-haspopup', 'dialog');
      button.append(img); button.addEventListener('click', () => this.preview(item, button, review));
      return button;
    }
    preview(item, trigger, review = item.modalReview) {
      const matched = this.galleryEntries.find(value => value.media.some(media => this.mediaKey(media) === this.mediaKey(item)));
      const entry = matched || this.addGalleryItem(item, review);
      if (!entry) return;
      this.previewIndex = this.galleryEntries.indexOf(entry);
      this.previewMediaIndex = Math.max(0, entry.media.findIndex(media => this.mediaKey(media) === this.mediaKey(item)));
      if (this.dialog?.open) { this.renderPreview(); return; }
      this.previewTrigger = trigger;
      this.previewOrigin = { reviewId: trigger.closest('[data-review-id]')?.dataset.reviewId, mediaIndex: [...(trigger.parentElement?.children || [])].indexOf(trigger) };
      const dialog = node('dialog', 'hollow-reviews__dialog');
      this.dialog = dialog;
      dialog.setAttribute('aria-label', this.strings.mediaDialog);
      const header = node('div', 'hollow-reviews__dialog-header');
      header.append(node('span', 'hollow-reviews__dialog-heading', this.strings.mediaDialog));
      const close = node('button', 'hollow-reviews__dialog-close', '×');
      close.type = 'button'; close.setAttribute('aria-label', this.strings.close);
      header.append(close);
      const body = node('div', 'hollow-reviews__dialog-body');
      this.previewStage = node('div', 'hollow-reviews__dialog-stage');
      this.previewDetails = node('div', 'hollow-reviews__dialog-details');
      body.append(this.previewStage, this.previewDetails);
      const footer = node('div', 'hollow-reviews__dialog-footer');
      this.previewPrevious = node('button', 'hollow-reviews__dialog-nav', '‹');
      this.previewNext = node('button', 'hollow-reviews__dialog-nav', '›');
      this.previewPrevious.type = this.previewNext.type = 'button';
      this.previewPrevious.setAttribute('aria-label', this.strings.previousReview);
      this.previewNext.setAttribute('aria-label', this.strings.nextReview);
      this.previewStatus = node('span', 'hollow-reviews__dialog-position');
      this.previewStatus.setAttribute('role', 'status'); this.previewStatus.setAttribute('aria-live', 'polite'); this.previewStatus.setAttribute('aria-atomic', 'true');
      footer.append(this.previewPrevious, this.previewStatus, this.previewNext);
      dialog.append(header, body, footer);
      close.addEventListener('click', () => dialog.close());
      this.previewPrevious.addEventListener('click', () => this.movePreview(-1));
      this.previewNext.addEventListener('click', () => this.movePreview(1));
      dialog.addEventListener('keydown', event => {
        if (event.key === 'Tab' && !event.altKey && !event.ctrlKey && !event.metaKey) {
          const focusable = [...dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])')].filter(element => !element.closest('[hidden], [inert]') && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
          const first = focusable[0], last = focusable.at(-1);
          if (first && ((event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) || (!event.shiftKey && document.activeElement === last))) {
            event.preventDefault(); (event.shiftKey ? last : first).focus({ preventScroll: true });
          }
          return;
        }
        if (event.altKey || event.ctrlKey || event.metaKey || event.target.closest('video, input, textarea, select')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); this.movePreview(event.key === 'ArrowRight' ? 1 : -1); }
      });
      const outside = event => { const rect = dialog.getBoundingClientRect(); return event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom); };
      dialog.addEventListener('pointerdown', event => { this.previewBackdropDown = outside(event); });
      dialog.addEventListener('click', event => { if (this.previewBackdropDown && outside(event)) dialog.close(); this.previewBackdropDown = false; });
      dialog.addEventListener('close', () => this.finishPreview(dialog), { once: true });
      this.append(dialog);
      this.renderPreview();
      this.lockPreviewScroll();
      try { dialog.showModal(); close.focus({ preventScroll: true }); } catch { this.finishPreview(dialog); }
    }
    movePreview(direction) {
      if (this.galleryEntries.length < 2) return;
      this.previewIndex = (this.previewIndex + direction + this.galleryEntries.length) % this.galleryEntries.length;
      this.previewMediaIndex = 0;
      this.renderPreview();
    }
    renderPreview() {
      const entry = this.galleryEntries[this.previewIndex], item = entry.media[this.previewMediaIndex];
      const restoreFocus = this.previewDetails.contains(document.activeElement) || this.previewStage.contains(document.activeElement);
      this.previewStage.querySelector('video')?.pause();
      const src = safeMedia(item.fullSizeUrl) || safeMedia(item.destinationUrl || item.url), videoUrl = item.kind === 'video' && safeMedia(item.videoUrl);
      const media = node(videoUrl ? 'video' : 'img', 'hollow-reviews__dialog-media');
      if (videoUrl) { media.src = videoUrl; media.poster = src; media.controls = true; media.playsInline = true; media.preload = 'metadata'; }
      else { media.src = src; media.alt = text(item.alt) || this.strings.imageAlt; media.decoding = 'async'; }
      this.previewStage.replaceChildren(media);
      this.previewDetails.replaceChildren(...(entry.review ? [this.card(entry.review, true)] : []));
      this.dialog.classList.toggle('hollow-reviews__dialog--media-only', !entry.review);
      if (entry.media.length > 1) {
        const thumbnails = node('div', 'hollow-reviews__dialog-thumbnails');
        thumbnails.setAttribute('aria-label', this.strings.reviewMedia);
        entry.media.forEach((value, index) => {
          const button = node('button', 'hollow-reviews__dialog-thumbnail'); button.type = 'button';
          button.setAttribute('aria-label', this.label('mediaNumber', { number: index + 1 }));
          button.setAttribute('aria-pressed', String(index === this.previewMediaIndex));
          if (value.kind === 'video') button.classList.add('has-video');
          const image = node('img'); image.src = safeMedia(value.destinationUrl || value.url || value.fullSizeUrl); image.alt = ''; image.width = 64; image.height = 64; image.loading = 'lazy';
          button.append(image);
          button.addEventListener('click', () => { this.previewMediaIndex = index; this.renderPreview(); this.previewDetails.querySelectorAll('.hollow-reviews__dialog-thumbnail')[index]?.focus({ preventScroll: true }); });
          thumbnails.append(button);
        });
        this.previewDetails.append(thumbnails);
      }
      this.previewPrevious.disabled = this.previewNext.disabled = this.galleryEntries.length < 2;
      this.previewStatus.textContent = this.label('reviewPosition', { number: this.previewIndex + 1, count: this.galleryEntries.length });
      this.previewDetails.scrollTop = 0;
      this.dialog.querySelector('.hollow-reviews__dialog-body').scrollTop = 0;
      if (restoreFocus) (this.previewNext.disabled ? this.dialog.querySelector('.hollow-reviews__dialog-close') : this.previewNext).focus({ preventScroll: true });
    }
    lockPreviewScroll() {
      const root = document.documentElement, body = document.body;
      this.previewScroll = { x: window.scrollX, y: window.scrollY, styles: [] };
      for (const [element, properties] of [[root, ['overflow', 'scroll-behavior']], [body, ['position', 'top', 'left', 'right', 'width', 'overflow']]]) {
        for (const property of properties) this.previewScroll.styles.push({ element, property, value: element.style.getPropertyValue(property), priority: element.style.getPropertyPriority(property) });
      }
      root.style.setProperty('overflow', 'hidden'); root.style.setProperty('scroll-behavior', 'auto');
      body.style.setProperty('position', 'fixed'); body.style.setProperty('top', `${-this.previewScroll.y}px`); body.style.setProperty('left', '0'); body.style.setProperty('right', '0'); body.style.setProperty('width', '100%'); body.style.setProperty('overflow', 'hidden');
    }
    finishPreview(dialog) {
      if (this.dialog !== dialog) return;
      dialog.querySelector('video')?.pause();
      dialog.remove(); this.dialog = null;
      const scroll = this.previewScroll;
      if (scroll) {
        for (const { element, property, value, priority } of scroll.styles) { if (property !== 'scroll-behavior') value ? element.style.setProperty(property, value, priority) : element.style.removeProperty(property); }
        window.scrollTo({ left: scroll.x, top: scroll.y, behavior: 'auto' });
        const behavior = scroll.styles.find(value => value.property === 'scroll-behavior');
        if (behavior?.value) behavior.element.style.setProperty(behavior.property, behavior.value, behavior.priority); else behavior?.element.style.removeProperty('scroll-behavior');
        this.previewScroll = null;
      }
      let target = this.previewTrigger;
      if (!target?.isConnected && this.isConnected) {
        const article = [...this.list.children].find(item => item.dataset.reviewId === this.previewOrigin?.reviewId);
        target = article?.querySelectorAll('.hollow-reviews__image')[this.previewOrigin?.mediaIndex] || article;
        if (!target) target = [this.search, this.filterToggle, this.list.firstElementChild, this.gallery.firstElementChild].find(element => element?.isConnected && !element.closest('[hidden], [inert]') && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
      }
      target?.focus({ preventScroll: true });
      this.previewTrigger = null;
      this.previewOrigin = null;
    }
    closePreview() {
      if (!this.dialog) return;
      const dialog = this.dialog;
      if (dialog.open) dialog.close();
      this.finishPreview(dialog);
    }
  }
  customElements.define('hollow-product-reviews', HollowProductReviews);
})();
